import React, { useEffect, useState, useCallback } from "react";
import {
    Card,
    Form,
    Button,
    Table,
    Row,
    Col,
    Spinner,
    Modal,
    ListGroup,
} from "react-bootstrap";
import {
    getFolders,
    addFolder,
    uploadLeadsCsv,
    getUsers,
} from "../../service/apiService";


function LeadManagement({ user }) {
    
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newFolder, setNewFolder] = useState({ name: "", description: "" });

    const [csvFile, setCsvFile] = useState(null);
    const [selectedFolder, setSelectedFolder] = useState("");
    const [assignStaff, setAssignStaff] = useState("");
    const [staffList, setStaffList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState("");
    // Load folders
    const loadFolders = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getFolders();
            if (res.status === "success") setFolders(res.data);
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    }, []);

    // Load staff
    const loadStaff = useCallback(async () => {
        try {
            const res = await getUsers();
            if (res)
                setStaffList(res.filter((u) => u.role === "staff" || u.role === "trainer"));
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => {
        loadFolders();
        loadStaff();
    }, [loadFolders, loadStaff]);

    // Handle folder creation
    const handleCreateFolder = async () => {
        if (!newFolder.name.trim()) {
            alert("Folder name required");
            return;
        }
        try {
            const payload = { name: newFolder.name.trim(), description: newFolder.description || '', created_by: user.id };
            
            const res = await addFolder(payload);

            if (res.status === 'success') {
                setShowAddModal(false);
                setNewFolder({ name: '', description: '' });
                await loadFolders();
            } else {
                alert('Failed to create folder: ' + (res.message || 'unknown'));
                console.error('folders_add.php response:', res);
            }
        } catch (err) {
            console.error(err);
            alert('Error while creating folder. Check console/network.');
        }
    };

    // Handle CSV upload
    const handleUpload = async (e) => {
        e.preventDefault();
        if (!csvFile || !selectedFolder || !assignStaff) {
            alert("Please select folder, staff and CSV file");
            return;
        }

        const formData = new FormData();
        formData.append("file", csvFile);
        formData.append("folder_id", selectedFolder);
        formData.append("assign_staff_id", assignStaff);
        formData.append("uploaded_by", user.id);

        setUploading(true);
        setMessage("");
        try {
            const res = await uploadLeadsCsv(formData);
            if (res.status === "success") {
                setMessage(`✅ ${res.inserted} leads uploaded successfully!`);
                setCsvFile(null);
                loadFolders();
            } else {
                setMessage(`❌ Upload failed: ${res.message}`);
            }
        } catch (err) {
            console.error(err);
            setMessage("❌ Error during upload.");
        }
        setUploading(false);
    };

    return (
        <div className="p-3">
            <Card className="shadow-sm p-4 mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">Lead Folder Management</h4>
                    <Button onClick={() => setShowAddModal(true)}>+ Create New Folder</Button>
                </div>

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : folders.length === 0 ? (
                    <p className="text-muted">No folders yet. Click "Create New Folder" to add one.</p>
                ) : (
                    <ListGroup>
                        {folders.map((f) => (
                            <ListGroup.Item key={f.id}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>{f.name}</strong>
                                        <div className="text-muted small">{f.description || "—"}</div>
                                    </div>
                                    <div className="text-end small text-muted">
                                        Leads: {f.lead_count || 0} <br />
                                        Created by: {f.created_by_name || "—"}
                                    </div>
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )}
            </Card>

            <Card className="shadow-sm p-4">
                <h5 className="mb-3">Upload Leads (CSV)</h5>
                <Form onSubmit={handleUpload}>
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Select Folder</Form.Label>
                                <Form.Select
                                    value={selectedFolder}
                                    onChange={(e) => setSelectedFolder(e.target.value)}
                                >
                                    <option value="">-- Select Folder --</option>
                                    {folders.map((f) => (
                                        <option key={f.id} value={f.id}>
                                            {f.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Assign to Staff</Form.Label>
                                <Form.Select
                                    value={assignStaff}
                                    onChange={(e) => setAssignStaff(e.target.value)}
                                >
                                    <option value="">-- Select Staff --</option>
                                    {staffList.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.name} ({s.position || s.role})
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Upload CSV File</Form.Label>
                                <Form.Control
                                    type="file"
                                    accept=".csv"
                                    onChange={(e) => setCsvFile(e.target.files[0])}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Button
                        type="submit"
                        variant="primary"
                        disabled={uploading}
                        className="mt-2"
                    >
                        {uploading ? "Uploading..." : "Upload Leads"}
                    </Button>
                </Form>

                {message && <div className="mt-3">{message}</div>}

                <div className="text-muted small mt-3">
                    <strong>CSV Format:</strong> name, phone, email, source, remarks
                </div>
            </Card>

            {/* Add Folder Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Create New Folder</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Folder Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={newFolder.name}
                            onChange={(e) =>
                                setNewFolder({ ...newFolder, name: e.target.value })
                            }
                            placeholder="e.g. BCA Completed Leads"
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={newFolder.description}
                            onChange={(e) =>
                                setNewFolder({ ...newFolder, description: e.target.value })
                            }
                            placeholder="Optional"
                        />
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                        Cancel
                    </Button>

                    <Button variant="primary" onClick={handleCreateFolder}>
                        Save Folder
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
}

export default LeadManagement;
